import { ArIcon, FullscreenIcon, RecapPanelIcon, ViewerContainer, ZoomInIcon, ZoomOutIcon, ResetIcon, RedoIcon, UndoIcon } from "./LayoutStyled";
import { Button } from "components/Atomic";
import { useZakeke, ZakekeViewer } from "zakeke-configurator-react";
import { quitFullscreen, launchFullscreen, useUndoRedoActions, findGroup, findStep, findAttribute } from "Helpers";
import { useRef, useState } from "react";
import useStore from "Store";
import { Dialog, useDialogManager } from "../dialogs/Dialogs";
import { ReactComponent as SearchPlusSolid } from '../../assets/icons/search-plus-solid.svg';
import { ReactComponent as SearchMinusSolid } from '../../assets/icons/search-minus-solid.svg';
import { ReactComponent as ExpandSolid } from '../../assets/icons/expand-solid.svg';
import { ReactComponent as BarsSolid } from '../../assets/icons/bars-solid.svg';
import { ReactComponent as ResetSolid } from '../../assets/icons/reset-alt-solid.svg';
import { ReactComponent as UndoSolid } from '../../assets/icons/undo-solid.svg';
import { ReactComponent as RedoSolid } from '../../assets/icons/redo-solid.svg';
import arIcon from '../../assets/images/ar_icon.png';
import ArDeviceSelectionDialog from "components/dialogs/ArDeviceSelectionDialog";
import RecapPanel from "components/widgets/RecapPanel";
import { UndoRedoStep } from "Interfaces";

const Viewer = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [recapPanel, setRecapPanel] = useState(true)
  const { isSceneLoading, IS_IOS, IS_ANDROID, getMobileArUrl, openArMobile, isSceneArEnabled, zoomIn, zoomOut, sellerSettings, reset } = useZakeke();
  const { showDialog, closeDialog } = useDialogManager();
  const { setIsLoading } = useStore();

  const switchFullscreen = () => {
    if ((document as any).fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement) {
      quitFullscreen(ref.current!);
    } else {
      launchFullscreen(ref.current!);
    }
  }

  const handleArClick = async () => {

    if (IS_ANDROID || IS_IOS) {
      setIsLoading(true);
      const url = await getMobileArUrl();
      setIsLoading(false);

      if (url)
        if (IS_IOS) {
          openArMobile(url);
        } else if (IS_ANDROID) {
          showDialog('open-ar', <Dialog>
            <Button
              style={{ display: 'block', width: '100%' }}
              onClick={() => {
                closeDialog('open-ar');
                openArMobile(url);
              }}>VIEW PRODUCT IN AR</Button>
          </Dialog>);
        }
    } else {
      showDialog('select-ar', <ArDeviceSelectionDialog />);
    }
  }

  const { setIsUndo, undoStack, setIsRedo, redoStack } = useStore();
  const undoRedoActions = useUndoRedoActions();

  const handleUndoClick = () => {
    setIsUndo(true);
    let actualUndoStep = undoStack.length > 0 ? undoStack.pop() : null;
    if (actualUndoStep && actualUndoStep.length > 0) {
      undoRedoActions.fillRedoStack(actualUndoStep);
      actualUndoStep.filter((x: UndoRedoStep) => x.direction === "undo").forEach((singleStep: UndoRedoStep) => handleUndoSingleStep(singleStep));
    }
    setIsUndo(false);
  }

  const { groups, undo, redo } = useZakeke();
  const { setSelectedGroupId, setSelectedStepId, setSelectedAttributeId } = useStore();
  const isMobile = window.innerWidth <= 768;

  const handleUndoSingleStep = (actualUndoStep: UndoRedoStep) => {
    if (actualUndoStep.id === null && !isMobile)
      return;
    if (actualUndoStep.type === "group")
      return setSelectedGroupId(findGroup(groups, actualUndoStep.id)?.id ?? null);
    if (actualUndoStep.type === "step")
      return setSelectedStepId(findStep(groups, actualUndoStep.id)?.id ?? null);
    if (actualUndoStep.type === "attribute")
      return (setSelectedAttributeId(findAttribute(groups, actualUndoStep.id)?.id ?? null));
    if (actualUndoStep.type === "option") {
      return undo();
    }
  }

  const handleRedoClick = () => {
    setIsRedo(true);
    let actualRedoStep = redoStack.length > 0 ? redoStack.pop() : null;
    if (actualRedoStep != null) {
      undoRedoActions.fillUndoStack(actualRedoStep);
      actualRedoStep.filter((x: UndoRedoStep) => x.direction === "redo").forEach(async (singleStep: UndoRedoStep) => handleRedoSingleStep(singleStep));
    }
    setIsRedo(false);
  }

  const handleRedoSingleStep = (actualRedoStep: { type: string, id: number | null, direction: string }) => {
    if (actualRedoStep.id === null && !isMobile)
      return;
    if (actualRedoStep.type === "group")
      return setSelectedGroupId(findGroup(groups, actualRedoStep.id)?.id ?? null);
    if (actualRedoStep.type === "step")
      return setSelectedStepId(findStep(groups, actualRedoStep.id)?.id ?? null);
    else if (actualRedoStep.type === "attribute")
      return (setSelectedAttributeId(findAttribute(groups, actualRedoStep.id)?.id ?? null));
    else if (actualRedoStep.type === "option")
      return redo();
  }


  return <ViewerContainer ref={ref}>
    {!isSceneLoading && <ZakekeViewer backgroundColor='#ffffff' />}

    <ZoomInIcon key={"zoomin"} hoverable onClick={zoomIn} ><SearchPlusSolid /></ZoomInIcon>
    <ZoomOutIcon key={"zoomout"} hoverable onClick={zoomOut} ><SearchMinusSolid /></ZoomOutIcon>
    <ResetIcon key={"reset"} hoverable onClick={reset}><ResetSolid /></ResetIcon>
    <UndoIcon key={"undo"} hoverable onClick={handleUndoClick}><UndoSolid /></UndoIcon>
    <RedoIcon key={"redo"} hoverable onClick={handleRedoClick}><RedoSolid /></RedoIcon>

    {!IS_IOS && <FullscreenIcon key={"fullscreen"} hoverable onClick={switchFullscreen} ><ExpandSolid /></FullscreenIcon>}
    {isSceneArEnabled() && <ArIcon key={"ar"} hoverable><img src={arIcon} alt='' onClick={handleArClick} /></ArIcon>}
    {sellerSettings?.isCompositionRecapEnabled && sellerSettings.isCompositionRecapVisibleFromStart && <RecapPanelIcon key={"recap"} onClick={() => setRecapPanel(!recapPanel)}><BarsSolid /></RecapPanelIcon>}
    {sellerSettings?.isCompositionRecapEnabled && recapPanel && <RecapPanel key={"recapPanel"} setRecapPanel={setRecapPanel} />}
  </ViewerContainer>
}

export default Viewer;